import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';

import { removeToken, removeUser, setToken, setUser } from '../../service/tokenServices';
import { login, logout, verifyOtp, updateProfile, getLoginOptions } from '../../service/request/authRequest';

export const getLoginOptionsAsyncThunk = createAsyncThunk('auth/getLoginOptions', async ({ email }) => {
    const response = await getLoginOptions(email);
    return response.data;
});

export const loginAsyncThunk = createAsyncThunk('auth/login', async ({ email, password, otp_method }) => {
    try {
        const response = await login(email, password, otp_method);
        if (response.data.success) {
            setToken(response.data.data.token);
            return response.data;
        } else {
            return Promise.reject(response.data.error);
        }
    } catch (error) {
        return Promise.reject(error);
    }
});

export const logoutAsyncThunk = createAsyncThunk('auth/logout', async () => {
    const response = await logout();
    return response.data;
});

export const verifyOtpAsyncThunk = createAsyncThunk('auth/verifyOtp', async ({ email, otp_code }, { rejectWithValue }) => {
    try {
        const response = await verifyOtp(email, otp_code);
        return response.data;
    } catch (error) {
        return rejectWithValue(error.response?.data || { message: error.message });
    }
});

export const updateProfileAsyncThunk = createAsyncThunk('auth/updateProfile', async (data, { rejectWithValue }) => {
    try {
        const response = await updateProfile(data);
        return response.data;
    } catch (error) {
        return rejectWithValue(error.response?.data || { message: error.message });
    }
});

export const authSlice = createSlice({
    name: 'auth',
    initialState: {
        user: null,
        isAuthenticated: false,
        errorMessage: null,
        loading: false,
        // Yeni state'ler
        requiresPhoneUpdate: false,
        requiresOtp: false,
        requiresPasswordChange: false,
        tempToken: null,
        tempUser: null,
        maskedPhone: null,
        maskedTarget: null,
        otpMethod: null,
        pendingEmail: null,
        // Login options state'leri
        loginOptions: null,
        loadingOptions: false,
        optionsError: null
    },
    reducers: {
        setStoreUser: (state, action) => {
            state.user = action.payload;
        },
        setIsAuthenticated: (state, action) => {
            state.isAuthenticated = action.payload;
        },
        clearErrorMessage: (state, action) => {
            state.errorMessage = null;
        },
        // Yeni reducer'lar
        setRequiresPhoneUpdate: (state, action) => {
            state.requiresPhoneUpdate = action.payload;
        },
        setRequiresOtp: (state, action) => {
            state.requiresOtp = action.payload;
        },
        setTempToken: (state, action) => {
            state.tempToken = action.payload;
        },
        setTempUser: (state, action) => {
            state.tempUser = action.payload;
        },
        setMaskedPhone: (state, action) => {
            state.maskedPhone = action.payload;
        },
        setMaskedTarget: (state, action) => {
            state.maskedTarget = action.payload;
        },
        setOtpMethod: (state, action) => {
            state.otpMethod = action.payload;
        },
        setPendingEmail: (state, action) => {
            state.pendingEmail = action.payload;
        },
        clearLoginState: (state) => {
            state.requiresPhoneUpdate = false;
            state.requiresOtp = false;
            state.requiresPasswordChange = false;
            state.tempToken = null;
            state.tempUser = null;
            state.maskedPhone = null;
            state.maskedTarget = null;
            state.otpMethod = null;
            state.pendingEmail = null;
            state.loginOptions = null;
            state.loadingOptions = false;
            state.optionsError = null;
        },
        clearLoginOptions: (state) => {
            state.loginOptions = null;
            state.loadingOptions = false;
            state.optionsError = null;
        },
        setRequiresPasswordChange: (state, action) => {
            state.requiresPasswordChange = action.payload;
        },
        completePhoneUpdate: (state) => {
            // Telefon güncellendikten sonra normal login akışına devam
            if (state.tempToken && state.tempUser) {
                setToken(state.tempToken);
                setUser(state.tempUser);
                state.user = state.tempUser;
                state.isAuthenticated = true;
                state.requiresPhoneUpdate = false;
                state.tempToken = null;
                state.tempUser = null;
            }
        }
    },
    extraReducers(builder) {
        builder
            // Login Options
            .addCase(getLoginOptionsAsyncThunk.pending, (state) => {
                state.loadingOptions = true;
                state.optionsError = null;
            })
            .addCase(getLoginOptionsAsyncThunk.fulfilled, (state, action) => {
                state.loadingOptions = false;
                state.loginOptions = action.payload.data?.options || null;
            })
            .addCase(getLoginOptionsAsyncThunk.rejected, (state, action) => {
                state.loadingOptions = false;
                state.optionsError = action.error.message;
            })
            // Login
            .addCase(loginAsyncThunk.pending, (state, action) => {
                state.loading = true;
            })
            .addCase(loginAsyncThunk.fulfilled, (state, action) => {
                const { data } = action.payload;
                const { email: loginEmail } = action.meta.arg; // Thunk'a gönderilen email
                state.loading = false;

                // OTP gerekiyor mu?
                if (data.requires_otp) {
                    state.requiresOtp = true;
                    state.pendingEmail = loginEmail; // Her zaman form'dan girilen gerçek email
                    state.maskedPhone = data.phone;
                    // Yeni alanlar - backend'den otp_method ve masked_target dönerse kullan
                    state.otpMethod = data.otp_method || (data.phone ? 'phone' : 'email');
                    // maskedTarget: backend'den gelen veya loginOptions'dan set edilmiş değer korunsun
                    if (data.masked_target || data.phone || data.masked_email) {
                        state.maskedTarget = data.masked_target || data.phone || data.masked_email;
                    }
                    return;
                }

                // Telefon güncelleme gerekiyor mu?
                if (data.requires_phone_update) {
                    state.requiresPhoneUpdate = true;
                    state.tempToken = data.token;
                    state.tempUser = data.user;
                    setToken(data.token);
                    return;
                }

                // Normal login
                state.user = data.user;
                setUser(data.user);
                state.isAuthenticated = true;
            })
            .addCase(loginAsyncThunk.rejected, (state, action) => {
                state.user = null;
                state.isAuthenticated = false;
                state.loading = false;
                removeToken();
                removeUser();
                state.errorMessage = 'Birşeyler yanlış gitti. Tekrar deneyiniz.';
            })
            .addCase(verifyOtpAsyncThunk.fulfilled, (state, action) => {
                const { data } = action.payload;

                // Şifre değişikliği gerekiyor mu?
                if (data.requires_password_change) {
                    state.requiresPasswordChange = true;
                    state.tempToken = data.token;
                    state.tempUser = data.user;
                    setToken(data.token);
                    state.requiresOtp = false;
                    state.pendingEmail = null;
                    state.maskedPhone = null;
                    state.maskedTarget = null;
                    state.otpMethod = null;
                    return;
                }

                state.user = data.user;
                setToken(data.token);
                setUser(data.user);
                state.isAuthenticated = true;
                state.requiresOtp = false;
                state.pendingEmail = null;
                state.maskedPhone = null;
                state.maskedTarget = null;
                state.otpMethod = null;
            })
            .addCase(updateProfileAsyncThunk.fulfilled, (state, action) => {
                const { data } = action.payload;
                // Kullanıcı bilgilerini güncelle
                if (state.tempUser) {
                    state.tempUser = { ...state.tempUser, phone: data.phone };
                }
                if (state.user) {
                    state.user = { ...state.user, phone: data.phone };
                    setUser(state.user);
                }
            })
            .addCase(logoutAsyncThunk.fulfilled, (state, action) => {
                state.user = null;
                state.isAuthenticated = false;
                state.requiresPhoneUpdate = false;
                state.requiresOtp = false;
                state.requiresPasswordChange = false;
                state.tempToken = null;
                state.tempUser = null;
                state.maskedPhone = null;
                state.maskedTarget = null;
                state.otpMethod = null;
                state.pendingEmail = null;
                state.loginOptions = null;
                state.loadingOptions = false;
                state.optionsError = null;
                removeToken();
                removeUser();
            });
    }
});

export const {
    setStoreUser,
    setIsAuthenticated,
    clearErrorMessage,
    setRequiresPhoneUpdate,
    setRequiresOtp,
    setRequiresPasswordChange,
    setTempToken,
    setTempUser,
    setMaskedPhone,
    setMaskedTarget,
    setOtpMethod,
    setPendingEmail,
    clearLoginState,
    clearLoginOptions,
    completePhoneUpdate
} = authSlice.actions;
export default authSlice.reducer;
